import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Spinner, Tabs, Text, VStack } from "@chakra-ui/react";
import ClientService from "@/services/ClientService";
import ScenarioService from "@/services/agency/ScenarioService";
import WorkflowService from "@/services/agency/WorkflowService";
import CampaignService from "@/services/agency/CampaignService";
import ClientSummaryHeader from "@/pages/clients/components/ClientSummaryHeader";
import ClientDataTab from "@/pages/clients/components/ClientDataTab";
import ClientScenariosTab from "@/pages/clients/components/ClientScenariosTab";
import ClientRecruitmentsTab from "@/pages/clients/components/ClientRecruitmentsTab";
import ClientCampaignsTab from "@/pages/clients/components/ClientCampaignsTab";
import ClientInteractionsTab from "@/pages/clients/components/ClientInteractionsTab";

const listItems = (data) => (Array.isArray(data) ? data : (data?.items ?? []));
const listTotal = (data) => (Array.isArray(data) ? data.length : (data?.totalCount ?? data?.items?.length ?? 0));

const hasType = (scenarios, type) =>
    scenarios.some((s) => s.type === type || s.agent?.type === type);

const ClientDetailPage = () => {
    const { id } = useParams();

    const [client, setClient] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    const [scenarios, setScenarios] = useState([]);
    const [workflows, setWorkflows] = useState([]);
    const [workflowsTotal, setWorkflowsTotal] = useState(0);
    const [workflowsLoading, setWorkflowsLoading] = useState(true);
    const [campaigns, setCampaigns] = useState([]);
    const [campaignsTotal, setCampaignsTotal] = useState(0);
    const [campaignsLoading, setCampaignsLoading] = useState(true);
    const [interactionsTotal, setInteractionsTotal] = useState(null);

    const [activeTab, setActiveTab] = useState("data");

    const fetchClient = useCallback(() => {
        ClientService.getById(id)
            .then((data) => (data?.id ? setClient(data) : setNotFound(true)))
            .catch(() => setNotFound(true))
            .finally(() => setIsLoading(false));
    }, [id]);

    const fetchScenarios = useCallback(() => {
        ScenarioService.getByOwner(id)
            .then((data) => setScenarios(listItems(data)))
            .catch(() => setScenarios([]));
    }, [id]);

    const fetchWorkflows = useCallback(() => {
        setWorkflowsLoading(true);
        WorkflowService.getByClient(id)
            .then((data) => { setWorkflows(listItems(data)); setWorkflowsTotal(listTotal(data)); })
            .catch(() => { setWorkflows([]); setWorkflowsTotal(0); })
            .finally(() => setWorkflowsLoading(false));
    }, [id]);

    const fetchCampaigns = useCallback(() => {
        setCampaignsLoading(true);
        CampaignService.getByOwner(id)
            .then((data) => { setCampaigns(listItems(data)); setCampaignsTotal(listTotal(data)); })
            .catch(() => { setCampaigns([]); setCampaignsTotal(0); })
            .finally(() => setCampaignsLoading(false));
    }, [id]);

    useEffect(() => {
        setIsLoading(true);
        setNotFound(false);
        fetchClient();
        fetchScenarios();
        fetchWorkflows();
        fetchCampaigns();
    }, [fetchClient, fetchScenarios, fetchWorkflows, fetchCampaigns]);

    const hasRecruiter = hasType(scenarios, "Recruiter");
    const hasSales = hasType(scenarios, "SalesManager");
    const neither = !hasRecruiter && !hasSales;
    const showRecruitments = hasRecruiter || neither || workflows.length > 0;
    const showCampaigns = hasSales || neither || campaigns.length > 0;

    useEffect(() => {
        if (activeTab === "recruitments" && !showRecruitments) setActiveTab("data");
        if (activeTab === "campaigns" && !showCampaigns) setActiveTab("data");
    }, [activeTab, showRecruitments, showCampaigns]);

    const modelsCount = useMemo(
        () => new Set(scenarios.filter((s) => s.agent?.id).map((s) => s.agent.id)).size,
        [scenarios],
    );

    if (isLoading) {
        return (
            <VStack w="full" py={12} align="center">
                <Spinner color="white" size="xl" />
            </VStack>
        );
    }

    if (notFound || !client) {
        return (
            <VStack w="full" py={12} align="center">
                <Text color="text.grey">Klient nie znaleziony.</Text>
            </VStack>
        );
    }

    return (
        <VStack w="full" py={6} gap={5} align="flex-start">
            <ClientSummaryHeader
                client={client}
                counters={{
                    scenarios: scenarios.length,
                    models: modelsCount,
                    recruitments: workflowsTotal,
                    campaigns: campaignsTotal,
                    interactions: interactionsTotal,
                }}
                onClientChanged={fetchClient}
            />

            <Tabs.Root w="full" value={activeTab} onValueChange={(e) => setActiveTab(e.value)}>
                <Tabs.List>
                    <Tabs.Trigger value="data">Dane klienta</Tabs.Trigger>
                    <Tabs.Trigger value="scenarios">Scenariusze</Tabs.Trigger>
                    {showRecruitments && <Tabs.Trigger value="recruitments">Rekrutacje</Tabs.Trigger>}
                    {showCampaigns && <Tabs.Trigger value="campaigns">Kampanie</Tabs.Trigger>}
                    <Tabs.Trigger value="interactions">Interakcje</Tabs.Trigger>
                </Tabs.List>

                <Tabs.Content value="data">
                    <ClientDataTab client={client} scenarios={scenarios} onClientChanged={fetchClient} />
                </Tabs.Content>

                <Tabs.Content value="scenarios">
                    <ClientScenariosTab clientId={id} scenarios={scenarios} onChanged={fetchScenarios} />
                </Tabs.Content>

                {showRecruitments && (
                    <Tabs.Content value="recruitments">
                        <ClientRecruitmentsTab
                            clientId={id}
                            scenarios={scenarios}
                            workflows={workflows}
                            isLoading={workflowsLoading}
                            onChanged={fetchWorkflows}
                        />
                    </Tabs.Content>
                )}

                {showCampaigns && (
                    <Tabs.Content value="campaigns">
                        <ClientCampaignsTab clientId={id} campaigns={campaigns} isLoading={campaignsLoading} />
                    </Tabs.Content>
                )}

                <Tabs.Content value="interactions">
                    <ClientInteractionsTab clientId={id} onCount={setInteractionsTotal} />
                </Tabs.Content>
            </Tabs.Root>
        </VStack>
    );
};

export default ClientDetailPage;
